import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";

import { X, Maximize2, Minimize2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import BryntumContext from "@/react/context/bryntumContext";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { EntityRecord } from "@/types/entity";
import ImageAnnotationEditor from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { Button } from "@/modern/components/ui/button-shadcn";
import { formatFile } from "@/modern/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

import { FileDetailModalProvider, useFileDetailModal } from "./context/FileDetailModalContext";
import { SummaryTab } from "./components/SummaryTab";
import { ImageViewer } from "./components/ImageViewer";
import { Toolbar } from "./components/Toolbar";
import { AnalyzeFileShell, AnalyzePanelContent } from "../AnalyzeFile/AnalyzeDialog";
import { AnalyzeFloatingPanel } from "../AnalyzeFile/components/AnalyzeFloatingPanel";
import { ExtractionPanel } from "./components/extraction/ExtractionPanel";
import { PromotePanel } from "./components/promote/PromotePanel";
import { AiInsightOverlay } from "./components/promote/components/AiInsightOverlay";
import { FileCollectionNavButtons } from "../../FileCollectionNavButtons";

const ENTITY_RECORD_NAME_MAP = {
  "taskId": "Task",
  "resourceId": "Resource",
  "clientId": "Client",
}

interface FileDetailModalProps {
  entityRecord: EntityRecord;
  file: FormattedFile | null;
  navigationFiles?: FormattedFile[];
  open: boolean;
  setOpen: (open: boolean) => void;
  autoOpenPromotePanel?: boolean;
}

const FileDetailModalContent = () => {
  const {
    file,
    open,
    setOpen,
    activeTab,
    setActiveTab,
    imageFile,
    isAnnotating,
    setIsAnnotating,
    project,
    entityRecord,
    isFullscreen,
    toggleFullscreen,
    showSpinner,
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,
    isAnalyzeDialogOpen,
    handleCloseAnalyzePanel,
    handleFileOpen,
    isExtractionPanelOpen,
    extractionStates,
    isPromotePanelOpen,
    isAnalyzePanelDetached,
    toggleAnalyzePanelDetach,
    files,
  } = useFileDetailModal();
  const [analyzeDockSlot, setAnalyzeDockSlot] = useState<HTMLDivElement | null>(null);
  const analyzeDockRef = useCallback((node: HTMLDivElement | null) => {
    setAnalyzeDockSlot(node);
  }, []);
  const { user } = useSwivuBryntumContext();

  // Create a dummy editor instance for BaseModal compatibility
  const editorInstance = useMemo(() => ({ editorRef: null }), []);

  // Create a dummy record for BaseModal (required prop)
  const dummyRecord = useMemo(() => ({ isPhantom: true }), []);

  const MODAL_SIZE_CONFIG = useMemo(() => ({
    width: isFullscreen ? "100vw" : "80vw",
    height: isFullscreen ? "100vh" : "90dvh",
    minWidth: isFullscreen ? "100vw" : "min(80vw, 900px)",
    minHeight: isFullscreen ? "100vh" : "min(90vh, 700px)",
    maxWidth: isFullscreen ? "100vw" : "80vw",
    maxHeight: isFullscreen ? "100vh" : "100dvh"
  }), [isFullscreen]);


  useEffect(() => {
    if (file && !file.canView) {
      setOpen(false);
    }
  }, [file])

  return (
    <>
      {/* Image Annotation Editor - rendered outside modal conditional */}
      {imageFile && isAnnotating && project && <ImageAnnotationEditor
        entityRecord={entityRecord}
        project={project}
        fileToAnnotate={imageFile}
        onClose={() => setIsAnnotating(false)}
        onAfterImageAnnotate={async (fileToAnnotate, annotatedImageRecord) => {
          if (fileToAnnotate.isCCImage) {
            await project.sync();
            const [f] = formatFile([annotatedImageRecord], user);
            handleFileOpen(f);
          }
        }}
      />}

      {!open || !file ? null : (

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "review" | "summary")}>
          <BaseModal
            title={file.name}
            description=""
            onClose={() => setOpen(false)}
            size={MODAL_SIZE_CONFIG}
            editorInstance={editorInstance}
            record={dummyRecord}
            headerIconCls=""
            modal={true}
            version=""
            className={isFullscreen ? "!rounded-none" : ""}
            hideMenu={true}
            headerRenderer={() => (
              <>
                <div className="border-b border-border/50 drag-handle py-4 text-center px-6 text-card-foreground">
                  {ENTITY_RECORD_NAME_MAP[entityRecord.entityType]} | {entityRecord.name}
                </div>
                <div className="flex items-center justify-between border-b border-border/50 drag-handle cursor-move">
                  <div className="flex-1">
                    <TabsList className="h-auto bg-transparent p-0 gap-0 w-full justify-start rounded-none">
                      <TabsTrigger
                        value="review"
                        className="relative rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6 py-3 data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                        activeElementClassName="bg-primary/10 rounded-none"
                      >
                        Review & Markup
                        {Object.values(extractionStates).filter((s) => !!s?.results).length > 0 && (
                          <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/15 text-primary text-[10px] font-semibold leading-none">
                            {Object.values(extractionStates).filter((s) => !!s?.results).length}
                          </span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger
                        value="summary"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6 py-3 data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                        activeElementClassName="bg-primary/10 rounded-none"
                      >
                        Summary & Share
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  <div className="flex items-center gap-2 pr-2">
                    <FileCollectionNavButtons
                      filesCount={files.length}
                      hasPrevious={hasPrevious}
                      hasNext={hasNext}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={toggleFullscreen}
                      aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                      {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setOpen(false)}
                      aria-label="Close modal"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          >
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Tab Content - Review tab (image viewer with optional extraction panel) */}
              <TabsContent value="review" className="flex flex-col flex-1 min-h-0 mt-0 overflow-hidden data-[state=inactive]:hidden">
                <div className="flex flex-1 min-h-0 overflow-hidden">
                  {/* Document preview (shrinks when extraction panel is open) */}
                  <div className="relative flex-1 p-[2em] overflow-hidden min-w-0">
                    <ImageViewer />
                    {!showSpinner && (!isAnalyzeDialogOpen || isAnalyzePanelDetached) && <Toolbar />}
                    <AnimatePresence>
                      {isPromotePanelOpen && <AiInsightOverlay />}
                    </AnimatePresence>
                  </div>

                  {/* Extraction panel (right side, animated) */}
                  <AnimatePresence>
                    {isExtractionPanelOpen && (
                      <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: "38%", opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="border-l border-border overflow-hidden shrink-0"
                      >
                        <ExtractionPanel />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Promote panel (right side, animated) */}
                  <AnimatePresence>
                    {isPromotePanelOpen && (
                      <motion.div
                        key="promote-panel"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: "38%", opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="border-l border-border overflow-hidden shrink-0"
                      >
                        <PromotePanel />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Analyze panel dock slot (content portaled from shared provider) */}
                  <AnimatePresence>
                    {isAnalyzeDialogOpen && !isAnalyzePanelDetached && (
                      <motion.div
                        key="analyze-panel-dock"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: "44%", opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="border-l border-border overflow-hidden shrink-0"
                      >
                        <div ref={analyzeDockRef} className="flex h-full min-h-0 flex-col" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </TabsContent>
              <TabsContent value="summary" className="flex-1 min-h-0 mt-0 overflow-auto data-[state=inactive]:hidden">
                <SummaryTab />
              </TabsContent>
            </div>
          </BaseModal>
        </Tabs>
      )}
      {file && isAnalyzeDialogOpen && (
        <AnalyzeFileShell
          open={isAnalyzeDialogOpen}
          file={file}
          entityRecord={entityRecord}
          onAfterCCFileUpload={(newFile) => {
            handleFileOpen(newFile);
          }}
        >
          {isAnalyzePanelDetached ? (
            <AnalyzeFloatingPanel>
              <AnalyzePanelContent
                onOpenChange={(next) => {
                  if (!next) handleCloseAnalyzePanel();
                }}
                unassignedInsightMode={false}
                insightBoardId={null}
                presentation="panel"
                isDetached
                onToggleDetach={toggleAnalyzePanelDetach}
              />
            </AnalyzeFloatingPanel>
          ) : (
            analyzeDockSlot &&
            createPortal(
              <AnalyzePanelContent
                onOpenChange={(next) => {
                  if (!next) handleCloseAnalyzePanel();
                }}
                unassignedInsightMode={false}
                insightBoardId={null}
                presentation="panel"
                isDetached={false}
                onToggleDetach={toggleAnalyzePanelDetach}
              />,
              analyzeDockSlot,
            )
          )}
        </AnalyzeFileShell>
      )}
    </>
  );
};

export const FileDetailModal = ({ file, navigationFiles, open, setOpen, entityRecord, autoOpenPromotePanel }: FileDetailModalProps) => {
  const { swivuAppGrants, project, editorHandler } = useContext(BryntumContext);

  if (!open) {
    return null;
  }

  return (
    <FileDetailModalProvider entityRecord={entityRecord} file={file} navigationFiles={navigationFiles} open={open} setOpen={setOpen} project={project} swivuAppGrants={swivuAppGrants} editorHandler={editorHandler} autoOpenPromotePanel={autoOpenPromotePanel}>
      <FileDetailModalContent />
    </FileDetailModalProvider>
  );
};
